import { useEffect, useState, type FormEvent } from 'react';
import type { SessionPayload } from '@wr/shared';
import { ApiError, errorMessage, post } from '../api.js';
import { Checkbox, Field, Notice, RecoveryCodes } from '../components/ui.js';
import { useLoaded } from '../session.js';
import { AuthLayout } from './AuthLayout.js';

interface Setup {
  secret: string;
  otpauthUrl: string;
  qrSvg: string;
}

/**
 * Two-step sign-in is required for everyone (decision of Oct 28, 2026):
 * install an authenticator app, scan the QR code, confirm one code, then
 * save the recovery codes.
 */
export function SetUpApp() {
  const { setSession, refresh } = useLoaded();
  const [setup, setSetup] = useState<Setup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ session: SessionPayload; recoveryCodes: string[] } | null>(
    null,
  );
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let current = true;
    post<Setup>('/auth/setup/start')
      .then((s) => current && setSetup(s))
      .catch((err: unknown) => {
        if (!current) return;
        if (err instanceof ApiError && err.code === 'unauthenticated') void refresh();
        else setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [refresh]);

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setDone(
        await post<{ session: SessionPayload; recoveryCodes: string[] }>('/auth/setup/confirm', {
          code,
        }),
      );
    } catch (err) {
      setCode('');
      setError(
        err instanceof ApiError && err.code === 'invalid_code'
          ? 'This code is not valid. Check that you scanned the QR code above, then enter the code the app shows now.'
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout title="Save your recovery codes">
        <Notice kind="ok">Two-step sign-in is set up.</Notice>
        <p>
          If you lose your phone, each of these codes lets you sign in once instead of the app code.
          Keep them somewhere safe (a password manager, or printed). They are shown only now; you
          can make new ones in My account.
        </p>
        <RecoveryCodes codes={done.recoveryCodes} />
        <Checkbox checked={saved} onChange={(e) => setSaved(e.target.checked)}>
          I saved these codes in a safe place
        </Checkbox>
        <button
          type="button"
          className="btn primary block"
          disabled={!saved}
          onClick={() => setSession(done.session)}
        >
          Continue to the portal
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set up two-step sign-in">
      {error && <Notice kind="error">{error}</Notice>}
      <ol className="steps">
        <li>
          <b>1</b>
          <div>
            <p>
              Install an authenticator app on your phone: <strong>Microsoft Authenticator</strong>,{' '}
              <strong>Google Authenticator</strong> or a similar app.
            </p>
          </div>
        </li>
        <li>
          <b>2</b>
          <div>
            <p>In the app, add an account and scan this QR code.</p>
            {setup ? (
              <>
                <img
                  className="qr"
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qrSvg)}`}
                  alt="QR code to scan with the authenticator app"
                  width={200}
                  height={200}
                />
                <p className="small muted">
                  Cannot scan? Choose to enter a key in the app, and type this key:
                </p>
                <p>
                  <code className="key" data-testid="setup-key">
                    {setup.secret}
                  </code>
                </p>
              </>
            ) : (
              !error && <div className="spinner" aria-label="Preparing the QR code" />
            )}
          </div>
        </li>
        <li>
          <b>3</b>
          <div>
            <form onSubmit={(e) => void confirm(e)} className="vstack">
              <Field
                label="Enter the 6-digit code the app shows"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9 ]{6,7}"
                maxLength={7}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <button type="submit" className="btn primary block" disabled={busy || !setup}>
                Confirm
              </button>
            </form>
          </div>
        </li>
      </ol>
    </AuthLayout>
  );
}
